import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import { useAllUsersMap } from '../user/useAllUsers';
import NestableLink from '../utils/NestableLink';
import { Link } from '../utils/router/v3Bridge';
import AvatarStack from './AvatarStack';
import Button from './Button';
import styles from './EntityRow.module.scss';
import HoverPreviewable from './HoverPreviewable';
import OverflowMenu from './OverflowMenu';
import SVGIcon from './SVGIcon';

interface EntityRowInnerProps {
  aboveTitle?: React.ReactNode;
  icon: React.ReactNode;
  meta: React.ReactNode;
  title: React.ReactNode;
  contributorIds?: string[];
  renderOverflowMenu?: (close: () => void) => React.ReactNode;
}

const EntityRowInner = ({
  aboveTitle,
  icon,
  title,
  meta,
  contributorIds = [],
  renderOverflowMenu,
}: EntityRowInnerProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const allUsers = useAllUsersMap();

  const users = _.compact(contributorIds.map((id) => allUsers[id])).slice(0, 3);

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>{icon}</div>
      <div className={styles.titleContainer}>
        {!!aboveTitle && (
          <header className={styles.aboveTitle}>{aboveTitle}</header>
        )}
        <span className={styles.title} role="heading" aria-level={5}>
          {title}
        </span>
      </div>
      <footer className={styles.contributors}>
        <AvatarStack link users={users} showHoverCard longHoverDelay noWrap />
      </footer>
      <footer className={styles.meta}>{meta}</footer>

      {renderOverflowMenu && (
        <aside
          className={classNames(styles.overflowMenu, {
            [styles.isOpen]: isOverflowMenuOpen,
          })}
        >
          <OverflowMenu
            preventCloseOnContentClick
            button={
              <Button
                className={styles.overflowMenuButton}
                aria-label="Actions"
              >
                <SVGIcon>{overflowMenuIcon}</SVGIcon>
              </Button>
            }
            onOpenChange={setIsOverflowMenuOpen}
            open={isOverflowMenuOpen}
          >
            <div className={styles.overflowMenuContent}>
              {renderOverflowMenu(() => setIsOverflowMenuOpen(false))}
            </div>
          </OverflowMenu>
        </aside>
      )}
    </div>
  );
};

type EntityRowPreview = {
  id: string;
  node: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  onActivePreviewIdChange: (id: string | null) => void;
  activePreviewId: string | null;
};

interface EntityRowPreviewableProps {
  preview: EntityRowPreview;
  children: React.ComponentProps<typeof HoverPreviewable>['children'];
}

const EntityRowPreviewable = ({
  preview,
  children,
}: EntityRowPreviewableProps) => (
  <HoverPreviewable
    id={preview.id}
    preview={preview.node}
    activeId={preview.activePreviewId}
    onActiveIdChange={preview.onActivePreviewIdChange}
    placement="right"
  >
    {children}
  </HoverPreviewable>
);

interface EntityRowContainerProps
  extends Omit<EntityRowInnerProps, 'linkProps'> {
  inList: boolean;
  path: string;
  preview?: EntityRowPreview;
  'aria-label'?: string;
}

const EntityRow = ({
  inList,
  preview,
  path,
  'aria-label': ariaLabel,
  ...innerProps
}: EntityRowContainerProps) => {
  return (
    <NestableLink
      to={path}
      className={classNames(styles.link, { [styles.inList]: inList })}
      aria-label={ariaLabel}
      role="article"
    >
      {preview ? (
        <EntityRowPreviewable preview={preview}>
          {(targetProps) => (
            <EntityRowInner
              {...innerProps}
              icon={
                <>
                  {innerProps.icon}
                  <Link
                    to={path}
                    className={styles.hoverTarget}
                    aria-hidden
                    tabIndex={-1}
                    {...targetProps}
                  />
                </>
              }
            />
          )}
        </EntityRowPreviewable>
      ) : (
        <EntityRowInner {...innerProps} />
      )}
    </NestableLink>
  );
};

export default EntityRow;
